import { useTestFunc } from '@carvana/test-attributes';
import Image from 'next/image';
import { useRouter } from 'next/router';

import useAnalyticEvent from '../../shared/hooks/useAnalyticEvent';
import { APP_BASE_PATH } from '../../shared/appBasePath';
import { buildNavigationUrl } from '../../shared/routes';
import { AppraisalStatus, AppraisalSummary, TileTypes } from './summaryShape';
import {
  BadgeWrapper,
  CardWrapper,
  ContentWrapper,
  ExpiredText,
  ImageWrap,
  StyledIndicatorBadge,
  StyledLink,
  VehicleValue,
  YearMake
} from './SummaryTile.styles';

const SummaryTileHelios = ({
  summary: {
    acquisitionName,
    appraisalId,
    canUpdateExpiredMileage,
    expirationDisplayDate,
    isOfferAccepted,
    make,
    model,
    status,
    vehicleId,
    vehicleImages: { mobileImage } = {},
    vehicleValue,
    year
  }
}: SummaryTileProps) => {
  const router = useRouter();
  const { recordActionClicked } = useAnalyticEvent();

  const urlOfferOrHub = buildNavigationUrl(
    isOfferAccepted && status !== AppraisalStatus.Rejected
      ? `${APP_BASE_PATH}/hub/${acquisitionName}`
      : `${APP_BASE_PATH}/offer/${appraisalId}`
  );
  const urlResubmit = buildNavigationUrl(
    canUpdateExpiredMileage
      ? `${APP_BASE_PATH}/offer/${appraisalId}`
      : `${APP_BASE_PATH}/offer/edit/${vehicleId}`
  );

  const yearMake = `${year} ${make} ${model}`;
  const formattedDate = new Date(expirationDisplayDate).toLocaleDateString();

  const getTileType = () => {
    if (status === AppraisalStatus.Rejected) return TileTypes.ActionRequired;
    if (status === AppraisalStatus.Active && !vehicleValue) return TileTypes.ActionRequired;
    if (status === AppraisalStatus.Expired) return TileTypes.OfferExpired;
    return TileTypes.Active;
  };
  const tileType = getTileType();

  const value = `$${vehicleValue?.toLocaleString()}`;

  const getTestAttributes = useTestFunc();

  const handleClickTile = () => {
    recordActionClicked('Landing Page', 'AppraisalCard', { status }, true);
    if (tileType === TileTypes.OfferExpired) {
      router.push(urlResubmit);
    } else {
      router.push(urlOfferOrHub);
    }
  };

  const badgeTypes = {
    [TileTypes.Active]: null,
    [TileTypes.OfferExpired]: (
      <BadgeWrapper priority="critical">
        <StyledIndicatorBadge priority="critical" size="md" />
        {TileTypes.OfferExpired}
      </BadgeWrapper>
    ),
    [TileTypes.OfferPending]: (
      <BadgeWrapper priority="informational">
        <StyledIndicatorBadge priority="informational" size="md" />
        {TileTypes.OfferPending}
      </BadgeWrapper>
    ),
    [TileTypes.ActionRequired]: (
      <BadgeWrapper priority="warning">
        <StyledIndicatorBadge priority="warning" size="md" />
        {TileTypes.ActionRequired}
      </BadgeWrapper>
    )
  };

  const mobileImageSrc = mobileImage?.includes('generic') ? null : mobileImage;

  return (
    <CardWrapper
      priority={tileType === TileTypes.ActionRequired ? 'warning' : 'informational'}
      {...getTestAttributes('SummaryTile', appraisalId.toString())}
      onClick={handleClickTile}
      image={
        <ImageWrap>
          {mobileImageSrc ? (
            <Image
              loader={({ src }) => src}
              src={mobileImageSrc}
              alt={yearMake}
              width={79}
              height={110}
              quality={100}
            />
          ) : null}
        </ImageWrap>
      }
    >
      <ContentWrapper>
        {badgeTypes[tileType]}
        <div>
          {tileType === TileTypes.Active && <VehicleValue>{value}</VehicleValue>}{' '}
          <ExpiredText>{tileType === TileTypes.Active && <>Expires {formattedDate}</>}</ExpiredText>
        </div>
        <YearMake>{yearMake}</YearMake>
        {tileType === TileTypes.Active && (
          <StyledLink size="sm" href={urlOfferOrHub}>
            View Offer
          </StyledLink>
        )}
        {tileType === TileTypes.OfferExpired && (
          <StyledLink size="sm" href={urlResubmit}>
            Refresh Offer
          </StyledLink>
        )}
        {tileType === TileTypes.ActionRequired && (
          <StyledLink size="sm" href={urlOfferOrHub}>
            View Details
          </StyledLink>
        )}
      </ContentWrapper>
    </CardWrapper>
  );
};

export type SummaryTileProps = {
  summary: AppraisalSummary;
};

export default SummaryTileHelios;
